import { makeActionCreatorObjParam } from '@trumedianetworks/tmn-core-components/src/util/redux-helpers';

import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import unset from 'lodash/unset';
import has from 'lodash/has';
import set from 'lodash/set';
import * as t from './action_types';
import { DETAIL_MODES } from '../../form/constants';
import { SELECT_MODES } from '../../table/table-constants';
import { apiAction } from '../../api/store/actions';
import { selectors } from '../';
import { actions as notificationActions } from '../../notification';

import {
    userValidationErrorsSelector,
    userStagedEditsSelector,
    userValidatedPathsSelector,
    userListFiltersSelector,
    bulkUserDataSelector,
    selectedUserIdsSelector,
} from './selectors';
import { filterDataByTemplate, generateDefaultData } from '../../util/data-helpers';
import { getRouter } from '../../routing/route-base';
import { flattenedKeys } from '../../util/object-helpers';
const setUserList = makeActionCreatorObjParam(t.SET_USER_LIST, 'data');
const setAllUserList = makeActionCreatorObjParam(t.SET_ALL_USER_LIST, 'data');
const updateUserDetails = makeActionCreatorObjParam(t.UPDATE_USER_DETAILS, 'data');
const addUserDetails = makeActionCreatorObjParam(t.ADD_USER_DETAILS, 'data');
const _setUserFilters = makeActionCreatorObjParam(t.SET_USER_FILTERS, 'filters');
const setStagedEditData = makeActionCreatorObjParam(t.SET_STAGED_EDITS, 'data');
export const resetUserData = makeActionCreatorObjParam(t.RESET_USER_DATA);
export const clearStagedEdits = makeActionCreatorObjParam(t.CLEAR_STAGED_EDITS);
export const clearValidationErrors = makeActionCreatorObjParam(t.CLEAR_VALIDATION_ERRORS);
export const setUserDetailMode = makeActionCreatorObjParam(t.SET_DETAIL_MODE, 'mode');

export const clearUserDevices = makeActionCreatorObjParam(t.CLEAR_USER_DEVICES);
export const setUserDevices = makeActionCreatorObjParam(t.SET_USER_DEVICES, 'userDevices');

export const setUserHistory = makeActionCreatorObjParam(t.SET_USER_HISTORY, 'userHistory');
export const clearUserHistory = makeActionCreatorObjParam(t.CLEAR_USER_HISTORY);
export const setUserHistoryIsLoading = makeActionCreatorObjParam(
    t.SET_USER_HISTORY_IS_LOADING,
    'isLoading'
);

export const selectUser = makeActionCreatorObjParam(t.SELECT_USER, 'eUserId');
export const unselectUser = makeActionCreatorObjParam(t.UNSELECT_USER, 'eUserId');
export const selectAllUsers = makeActionCreatorObjParam(t.SELECT_ALL_USERS);
export const unselectAllUsers = makeActionCreatorObjParam(t.UNSELECT_ALL_USERS);

const setValidationErrors = makeActionCreatorObjParam(t.SET_VALIDATION_ERRORS, 'errors');
const setValidatedPaths = makeActionCreatorObjParam(t.SET_VALIDATED_PATHS, 'validatedPaths');
const setBulkApplyPaths = makeActionCreatorObjParam(t.SET_BULK_APPLY_PATHS, 'bulkApplyPaths');

const setIsSaving = makeActionCreatorObjParam(t.SET_IS_SAVING, 'isSaving');

const setTemplateListIsLoading = makeActionCreatorObjParam(
    t.SET_TEMPLATE_LIST_IS_LOADING,
    'isLoading'
);

export const setUserSelectMode = makeActionCreatorObjParam(t.SET_SELECT_MODE, 'selectMode');

const _setActiveAuthSite = makeActionCreatorObjParam(t.SET_ACTIVE_AUTH_SITE, 'activeAuthSite');

export const toggleBulkApplyForPath =
    ({ path }) =>
    (dispatch, getState) => {
        const bulkApplyPaths = selectors.bulkUserApplyPathsSelector(getState());
        const newBulkApplyPaths = bulkApplyPaths.includes(path)
            ? bulkApplyPaths.filter((p) => p !== path)
            : [path, ...bulkApplyPaths];
        dispatch(setBulkApplyPaths({ bulkApplyPaths: newBulkApplyPaths }));
    };

export const setActiveAuthSite = (params) => (dispatch, getState) => {
    dispatch(fetchUserConfigTemplates());
    dispatch(_setActiveAuthSite(params));
    dispatch(setUserDetailMode({ mode: DETAIL_MODES.VIEW }));
    dispatch(clearUserData());
};

export const setUserConfigTemplates = makeActionCreatorObjParam(
    t.SET_USER_CONFIG_TEMPLATES,
    'templates'
);

export const fetchUserConfigTemplates = () => (dispatch, getState) => {
    // little bit of order of operations issue here.
    // we need to fetch the user templates any time the site changes, but
    // we don't necessarily have the list of ids when the active auth site is first set (because it can come from the route etc)
    const eSiteId = selectors.activeAuthSiteIdSelector(getState());
    dispatch(setTemplateListIsLoading({ isLoading: true }));
    if (eSiteId) {
        dispatch(
            apiAction({
                url: '/v2/api/user-admin/user-config-templates',
                method: 'POST',
                data: { eSiteId },
                onSuccess: (templates) => setUserConfigTemplates({ templates }),
                onFailure: () => {
                    dispatch(setTemplateListIsLoading({ isLoading: false }));
                    console.log('Error occured fetching user config templates');
                },
                label: t.FETCH_USER_CONFIG_TEMPLATES,
            })
        );
    }
};

export const setStagedEdits = ({ data }) => {
    if (data) {
        const clonedData = cloneDeep(data);
        return setStagedEditData({ data: clonedData });
    }
    return setStagedEditData({ data: null });
};

export const setStagedEditByPath = (path, value) => (dispatch, getState) => {
    const state = getState();
    const stagedEdits = userStagedEditsSelector(state);
    const currentValidationErrors = userValidationErrorsSelector(state);
    const validatedPaths = userValidatedPathsSelector(state);
    if (has(currentValidationErrors, path)) {
        const clonedErrors = cloneDeep(currentValidationErrors);
        unset(clonedErrors, path);
        dispatch(setValidationErrors({ errors: clonedErrors }));
    }
    if (validatedPaths.includes(path)) {
        const newValidatedPaths = validatedPaths.filter((p) => p !== path);
        dispatch(setValidatedPaths({ validatedPaths: newValidatedPaths }));
    }
    // not sure if we have to, but we could clear previously passed validations or errors here
    const clonedEdits = cloneDeep(stagedEdits);
    set(clonedEdits, path, value);
    dispatch(setStagedEditData({ data: clonedEdits }));
};

export const validatePartialDetails = (partialUserData) => (dispatch, getState) => {
    dispatch(
        apiAction({
            url: '/v2/api/user-admin/user-validate-partial',
            method: 'POST',
            data: partialUserData,
            onSuccess: ({ isValid, errors = {} }) => {
                const state = getState();
                const currentValidationErrors = userValidationErrorsSelector(state) || {};
                // need to combine with previous errors OR clear previous matching errors
                if (!isValid && errors) {
                    // add errors, note merge mutates errors
                    merge(errors, currentValidationErrors);
                    dispatch(setValidationErrors({ errors }));
                }
                // on success we need to only clear or set the keys we were given in partialUserData
                if (isValid) {
                    // remove errors for partialUserData
                    const newErrors = cloneDeep(currentValidationErrors);
                    const validatedPaths = flattenedKeys(partialUserData);
                    // todo here and possibly elsewhere, we need to remove the errors parent obj if all
                    // error sub keys have been cleared
                    unset(newErrors, validatedPaths);
                    dispatch(setValidationErrors({ errors: newErrors }));
                    dispatch(setValidatedPaths({ validatedPaths }));
                }
            },
            onFailure: () => {
                console.log('Error occured checking partial validation of user data');
            },
            label: t.VALIDATE_PARTIAL_USER_DETAILS,
        })
    );
};

export const fetchUserList = () => (dispatch, getState) => {
    const eSiteId = selectors.activeAuthSiteIdSelector(getState());
    const userFilters = selectors.userListFiltersSelector(getState());
    const active = userFilters?.showInactive ? null : 1;
    const filters = {
        active,
    };
    if (eSiteId) {
        const params = { eSiteId, filters };
        dispatch(
            apiAction({
                url: '/v2/api/user-admin/users',
                method: 'POST',
                data: params,
                onSuccess: (data) => setUserList({ data }),
                onFailure: () => {
                    console.log('Error occured fetching user data');
                },
                label: t.FETCH_USER_LIST,
            })
        );
    }
};

export const setUserFilters = (filters) => (dispatch, getState) => {
    dispatch(_setUserFilters({ filters }));
    dispatch(fetchUserList());
};

export const fetchAllUserList = () => (dispatch, getState) => {
    const filters = selectors.userListFiltersSelector(getState()) || {};
    const { allUserSearch, showInactive } = filters;
    // slightly weird but to show inactive AND active, we need to send active as null
    // otherwiuse active as 1
    const active = showInactive ? null : 1;
    const search = allUserSearch?.trim();
    const MIN_SEARCH_CHARS = 4;
    if (!(search && search.length >= MIN_SEARCH_CHARS)) {
        return;
    }
    const params = { filters: { active, search } };
    dispatch(
        apiAction({
            url: '/v2/api/user-admin/all-users',
            method: 'POST',
            data: params,
            onSuccess: (data) => setAllUserList({ data }),
            onFailure: () => {
                console.log('Error occured fetching all users');
            },
            label: t.FETCH_ALL_USER_LIST,
        })
    );
    //}
};

export const setAllUserFilters = (filters) => (dispatch, getState) => {
    // note intentionally re-using filters here so it sticks on nav
    dispatch(_setUserFilters({ filters }));
    dispatch(fetchAllUserList());
};

export const userDetails =
    ({ eUserId }) =>
    (dispatch, getState) => {
        dispatch(clearUserData());
        dispatch(selectUser({ eUserId }));
    };

export const editUserDetails =
    ({ eUserId }) =>
    (dispatch, getState) => {
        dispatch(userDetails({ eUserId }));
        dispatch(setUserDetailMode({ mode: DETAIL_MODES.EDIT }));
    };

export const exitBulkEditMode = () => (dispatch, getState) => {
    dispatch(clearUserData());
    dispatch(setBulkApplyPaths({ bulkApplyPaths: [] }));
    dispatch(setUserSelectMode({ selectMode: SELECT_MODES.SINGLE }));
};

export const viewBulkUserDetails = () => (dispatch, getState) => {
    dispatch(setUserDetailMode({ mode: DETAIL_MODES.VIEW }));
};

export const viewUserDetails =
    ({ eUserId }) =>
    (dispatch, getState) => {
        dispatch(userDetails({ eUserId }));
        dispatch(setUserDetailMode({ mode: DETAIL_MODES.VIEW }));
    };

export const newUser = () => (dispatch, getState) => {
    dispatch(exitBulkEditMode());
    dispatch(setUserDetailMode({ mode: DETAIL_MODES.NEW }));
};

export const bulkEditUserSave = () => (dispatch, getState) => {
    const state = getState();
    const bulkData = bulkUserDataSelector(state);
    const eSiteId = selectors.activeAuthSiteIdSelector(getState());
    const editData = { ...bulkData, eSiteId };
    dispatch(setIsSaving({ isSaving: true }));
    dispatch(
        apiAction({
            url: '/v2/api/user-admin/bulk-user-edit',
            method: 'POST',
            data: editData,
            onSuccess: (responseData) => {
                // todo hmm, else what? throw error
                const { users } = responseData;
                if (users) {
                    dispatch(
                        notificationActions.showSnackbar({
                            snackbarMessage: `Bulk User edit successful.`,
                            snackbarType: 'success',
                        })
                    );
                    handleBulkUserEditSuccess(dispatch, getState, users);
                }
                dispatch(setIsSaving({ isSaving: false }));
            },

            onFailure: (err, data = {}) => {
                const { validationFailed, errors } = data;
                if (validationFailed && errors) {
                    dispatch(setValidationErrors({ errors }));
                }
                dispatch(setIsSaving({ isSaving: false }));
                console.error('Error occured saving user', err, data);
                dispatch(
                    notificationActions.showSnackbar({
                        snackbarMessage: `Error bulk saving users`,
                        snackbarType: 'error',
                    })
                );
            },
            label: t.BULK_EDIT_USER,
        })
    );
};

export const bulkActivateUser = () => (dispatch, getState) => {
    const state = getState();
    const eUserIds = selectedUserIdsSelector(state);
    const eSiteId = selectors.activeAuthSiteIdSelector(getState());
    const editData = { eUserIds, eSiteId };
    dispatch(setIsSaving({ isSaving: true }));
    dispatch(
        apiAction({
            url: '/v2/api/user-admin/bulk-user-activate',
            method: 'POST',
            data: editData,
            onSuccess: (responseData) => {
                const { users } = responseData;
                if (users) {
                    dispatch(
                        notificationActions.showSnackbar({
                            snackbarMessage: `Bulk activate successful.`,
                            snackbarType: 'success',
                        })
                    );
                    handleBulkUserEditSuccess(dispatch, getState, users);
                }
                dispatch(setIsSaving({ isSaving: false }));
            },

            onFailure: (err, data = {}) => {
                dispatch(setIsSaving({ isSaving: false }));
                console.error('Error occured activating users', err, data);
                dispatch(
                    notificationActions.showSnackbar({
                        snackbarMessage: `Error bulk activating users`,
                        snackbarType: 'error',
                    })
                );
            },
            label: t.BULK_ACTIVATE_USER,
        })
    );
};
export const bulkDeactivateUser = () => (dispatch, getState) => {
    const state = getState();
    const eUserIds = selectedUserIdsSelector(state);
    const eSiteId = selectors.activeAuthSiteIdSelector(getState());
    const editData = { eUserIds, eSiteId };
    dispatch(setIsSaving({ isSaving: true }));
    dispatch(
        apiAction({
            url: '/v2/api/user-admin/bulk-user-deactivate',
            method: 'POST',
            data: editData,
            onSuccess: (responseData) => {
                const { users } = responseData;
                if (users) {
                    dispatch(
                        notificationActions.showSnackbar({
                            snackbarMessage: `Bulk deactivate successful.`,
                            snackbarType: 'success',
                        })
                    );
                    handleBulkUserEditSuccess(dispatch, getState, users);
                }
                dispatch(setIsSaving({ isSaving: false }));
            },

            onFailure: (err, data = {}) => {
                dispatch(setIsSaving({ isSaving: false }));
                console.error('Error occured deactivating users', err, data);
                dispatch(
                    notificationActions.showSnackbar({
                        snackbarMessage: `Error bulk deactivating users`,
                        snackbarType: 'error',
                    })
                );
            },
            label: t.BULK_DEACTIVATE_USER,
        })
    );
};

export const fetchUserDevices =
    ({ eUserId, eSiteId }) =>
    (dispatch, getState) => {
        const params = { eUserId, eSiteId };

        dispatch(
            apiAction({
                url: '/v2/api/user-admin/list-devices',
                method: 'POST',
                data: params,
                onSuccess: ({ userDevices = [] } = {}) => {
                    // only set the devices IF the user currently matches the original request
                    // this ought to prevent getting out of sync
                    const userData = selectors.selectedUserDataSelector(getState());
                    if (eUserId === userData?.eUserId) {
                        dispatch(setUserDevices({ userDevices }));
                    }
                },
                onFailure: (err, data = {}) => {
                    console.error('error fetching user devices', err);
                },
                label: t.FETCH_USER_DEVICES,
            })
        );
    };

export const verifyDevice = (params) => (dispatch, getState) => {
    dispatch(
        apiAction({
            url: '/v2/api/user-admin/verify-device',
            method: 'POST',
            data: params,
            onSuccess: (responseData) => {
                const { userDevices } = responseData;
                if (userDevices) {
                    dispatch(
                        notificationActions.showSnackbar({
                            snackbarMessage: `Device verified.`,
                            snackbarType: 'success',
                        })
                    );
                    dispatch(setUserDevices({ userDevices }));
                }
            },
            onFailure: (err, data = {}) => {
                console.log(err);
            },
            label: t.VERIFY_USER_DEVICE,
        })
    );
};

export const removeDevice = (params) => (dispatch, getState) => {
    dispatch(
        apiAction({
            url: '/v2/api/user-admin/remove-device',
            method: 'POST',
            data: params,
            onSuccess: (responseData) => {
                const { userDevices } = responseData;
                if (userDevices) {
                    dispatch(
                        notificationActions.showSnackbar({
                            snackbarMessage: `Device removed.`,
                            snackbarType: 'success',
                        })
                    );
                    dispatch(setUserDevices({ userDevices }));
                }
            },
            onFailure: (err, data = {}) => {
                console.log(err);
            },
            label: t.REMOVE_USER_DEVICE,
        })
    );
};

export const cancelUserChanges = () => (dispatch, getState) => {
    dispatch(clearUserData());
    dispatch(setUserDetailMode({ mode: DETAIL_MODES.VIEW }));
    const router = getRouter();
    const authSite = selectors.activeAuthSiteNameSelector(getState());
    router.navigateByName('user-list', { authSite });
};

export const addUser = (params) => (dispatch, getState) => {
    const { data } = params;
    const eSiteId = selectors.activeAuthSiteIdSelector(getState());
    const userTemplate = selectors.userConfigTemplateSelector(getState()) || {};
    const filteredData = filterDataByTemplate(userTemplate, data);
    const addNewData = { ...filteredData, eSiteId };
    dispatch(setIsSaving({ isSaving: true }));
    dispatch(
        apiAction({
            url: '/v2/api/user-admin/user-add',
            method: 'POST',
            data: addNewData,
            onSuccess: (responseData) => {
                const { userData } = responseData;
                if (userData) {
                    dispatch(
                        notificationActions.showSnackbar({
                            snackbarMessage: `User added.`,
                            snackbarType: 'success',
                        })
                    );
                    handleUserAddSuccess(dispatch, getState, userData);
                }
                dispatch(setIsSaving({ isSaving: false }));
            },
            onFailure: (err, data = {}) => {
                const { validationFailed, errors } = data;
                dispatch(setIsSaving({ isSaving: false }));
                if (validationFailed && errors) {
                    dispatch(setValidationErrors({ errors }));
                }
                console.error('Error occured adding new user', err, data);
                dispatch(
                    notificationActions.showSnackbar({
                        snackbarMessage: `Error adding new user`,
                        snackbarType: 'error',
                    })
                );
            },
            label: t.ADD_USER_DETAILS,
        })
    );
};

export const saveUser = (params) => (dispatch, getState) => {
    const { data } = params;
    const { eSiteId, eUserId } = data;
    const userTemplate = selectors.userConfigTemplateSelector(getState()) || {};
    const filteredData = filterDataByTemplate(userTemplate, data);
    const editData = { ...filteredData, eSiteId, eUserId };
    dispatch(setIsSaving({ isSaving: true }));
    dispatch(
        apiAction({
            url: '/v2/api/user-admin/user-edit',
            method: 'POST',
            data: editData,
            onSuccess: (responseData) => {
                // todo hmm, else what? throw error
                const { userData } = responseData;
                if (userData) {
                    dispatch(
                        notificationActions.showSnackbar({
                            snackbarMessage: `User edit successful.`,
                            snackbarType: 'success',
                        })
                    );
                    handleUserEditSuccess(dispatch, getState, userData);
                }
                dispatch(setIsSaving({ isSaving: false }));
            },

            onFailure: (err, data = {}) => {
                const { validationFailed, errors } = data;
                if (validationFailed && errors) {
                    dispatch(setValidationErrors({ errors }));
                }
                dispatch(setIsSaving({ isSaving: false }));
                console.error('Error occured saving user', err, data);
                dispatch(
                    notificationActions.showSnackbar({
                        snackbarMessage: `Error saving user`,
                        snackbarType: 'error',
                    })
                );
            },
            label: t.EDIT_USER_DETAILS,
        })
    );
};

export const activateUser = (data) => (dispatch, getState) => {
    const eSiteId = selectors.activeAuthSiteIdSelector(getState());

    dispatch(
        apiAction({
            url: '/v2/api/user-admin/user-activate',
            method: 'POST',
            data: { eSiteId, ...data },
            onSuccess: (responseData) => {
                const { userData } = responseData;
                if (userData) {
                    dispatch(
                        notificationActions.showSnackbar({
                            snackbarMessage: `Activate user successful.`,
                            snackbarType: 'success',
                        })
                    );
                    handleUserEditSuccess(dispatch, getState, userData);
                }
            },

            onFailure: (err, data = {}) => {
                console.error('Error occured activating user', err, data);
                dispatch(
                    notificationActions.showSnackbar({
                        snackbarMessage: `Error activating user`,
                        snackbarType: 'error',
                    })
                );
            },
            label: t.ACTIVATE_USER,
        })
    );
};

export const deactivateUser = (data) => (dispatch, getState) => {
    const eSiteId = selectors.activeAuthSiteIdSelector(getState());
    dispatch(
        apiAction({
            url: '/v2/api/user-admin/user-deactivate',
            method: 'POST',
            data: { eSiteId, ...data },
            onSuccess: (responseData) => {
                const { userData } = responseData;
                if (userData) {
                    dispatch(
                        notificationActions.showSnackbar({
                            snackbarMessage: `Deactivate user successful.`,
                            snackbarType: 'success',
                        })
                    );
                    handleUserEditSuccess(dispatch, getState, userData);
                }
            },

            onFailure: (err, data = {}) => {
                console.error('Error occured activating user', err, data);
                dispatch(
                    notificationActions.showSnackbar({
                        snackbarMessage: `Error deactivating user`,
                        snackbarType: 'error',
                    })
                );
            },
            label: t.DEACTIVATE_USER,
        })
    );
};

export const navigateResetPassword = ({ authSite }) => {
    const router = getRouter();
    router.navigateByName('reset-password-site', { authSite });
    return {};
};

export const resetPassword = (data) => (dispatch, getState) => {
    const eSiteId = selectors.activeAuthSiteIdSelector(getState());

    dispatch(
        apiAction({
            url: '/v2/api/user-admin/user-reset-password',
            method: 'POST',
            data: { eSiteId, ...data },
            onSuccess: (responseData) => {
                const { success } = responseData;
                if (success) {
                    dispatch(
                        notificationActions.showSnackbar({
                            snackbarMessage: `Password reset successful.`,
                            snackbarType: 'success',
                        })
                    );
                }
            },

            onFailure: (err, data = {}) => {
                console.error('Error occured resetting password', err, data);
                dispatch(
                    notificationActions.showSnackbar({
                        snackbarMessage: `Error resetting password`,
                        snackbarType: 'error',
                    })
                );
            },
            label: t.RESET_PASSWORD,
        })
    );
};

export const actAsUser = (data) => (dispatch, getState) => {
    const eSiteId = selectors.activeAuthSiteIdSelector(getState());

    dispatch(
        apiAction({
            url: '/v2/api/user-admin/act-as-user',
            method: 'POST',
            data: { eSiteId, ...data },
            onSuccess: (responseData) => {
                const { url, success } = responseData;
                window.open(url, '_blank');
                if (success) {
                    dispatch(
                        notificationActions.showSnackbar({
                            snackbarMessage: `Act as user successful.`,
                            snackbarType: 'success',
                        })
                    );
                }
            },

            onFailure: (err, data = {}) => {
                console.error('Error occured acting as user', err, data);
                dispatch(
                    notificationActions.showSnackbar({
                        snackbarMessage: `Error acting as user`,
                        snackbarType: 'error',
                    })
                );
            },
            label: t.RESET_PASSWORD,
        })
    );
};

export const fetchUserHistory =
    ({ eUserId, eSiteId }) =>
    (dispatch, getState) => {
        const params = { eUserId, eSiteId };
        dispatch(setUserHistoryIsLoading({ isLoading: true }));
        dispatch(clearUserHistory());
        dispatch(
            apiAction({
                url: '/v2/api/user-admin/user-history',
                method: 'POST',
                data: params,
                onSuccess: ({ userHistory = [] } = {}) => {
                    const userData = selectors.selectedUserDataSelector(getState());
                    if (eUserId === userData?.eUserId) {
                        dispatch(setUserHistory({ userHistory }));
                    }
                    dispatch(setUserHistoryIsLoading({ isLoading: false }));
                },
                onFailure: (err, data = {}) => {
                    console.error('error fetching user history', err);
                    dispatch(setUserHistoryIsLoading({ isLoading: false }));
                },
                label: t.FETCH_USER_HISTORY,
            })
        );
    };

export const clearUserData = () => (dispatch, getState) => {
    dispatch(clearValidationErrors());
    dispatch(unselectAllUsers());
    dispatch(setStagedEdits({ data: null }));
    dispatch(setValidatedPaths({ validatedPaths: null }));
    dispatch(clearUserDevices());
    dispatch(clearUserHistory());
    dispatch(setUserHistoryIsLoading({ isLoading: false }));
};

function handleUserAddSuccess(dispatch, getState, userData) {
    dispatch(clearUserData());
    dispatch(addUserDetails({ data: userData }));
    // const userTemplate = selectors.userConfigTemplateSelector(getState()) || {};
    // const defaultUserData = generateDefaultData(userTemplate);
    // dispatch(setStagedEdits({ data: defaultUserData }));
    // this differs from edit in that we make no change to the route so that
    // more users can be added easily
}

function handleUserEditSuccess(dispatch, getState, userData) {
    dispatch(setStagedEdits({ data: null }));
    dispatch(clearValidationErrors());
    dispatch(updateUserDetails({ data: [userData] }));
    dispatch(setUserDetailMode({ mode: DETAIL_MODES.VIEW }));
    dispatch(fetchUserHistory({ eUserId: userData.eUserId, eSiteId: userData.eSiteId }));
    // todo do we need this since we're not changing route now
    const router = getRouter();
    const authSite = selectors.activeAuthSiteNameSelector(getState());
    router.navigateByName('user-detail', { eUserId: userData.eUserId, authSite });
}

function handleBulkUserEditSuccess(dispatch, getState, updatedUsers) {
    dispatch(updateUserDetails({ data: [...updatedUsers] }));
    dispatch(exitBulkEditMode());
    // // todo do we need this since we're not changing route now
    // const router = getRouter();
    // const authSite = selectors.activeAuthSiteNameSelector(getState());
    // router.navigateByName('user-detail', { eUserId: userData.eUserId, authSite });
}
